import { isAxiosError } from 'axios';
import { useEffect, useMemo, useState } from 'react';
import type {
  CatalogRequestFieldSpec,
  CatalogRequestFields,
  CatalogRequestPreview,
  CatalogRequestSchema,
  CatalogRequestTypeSpec,
  FulfillmentAdminClient,
  FulfillmentOrderLine,
} from '@/infrastructure/FulfillmentAdminClient';
import { useModalDialog } from '@/pages/fulfillment/components/useModalDialog';
import {
  COGS_REPORT_CHANNEL,
  sendFailureOf,
  slackLineParts,
  useRequestIdForSend,
} from '@/pages/fulfillment/orders/components/CogsReportDialog';
import { LinePicker } from '@/pages/fulfillment/orders/components/LinePicker';

export const CATALOG_REQUEST_TITLE = 'Catalog Request';

/** Both posts go to the same channel; the catalog request names it the way the COGS report does. */
export const CATALOG_REQUEST_CHANNEL = COGS_REPORT_CHANNEL;

/** How long typing pauses before the preview is asked for again, so it is not rebuilt on every key. */
const PREVIEW_DELAY_MS = 250;

/** A note or a message gets a box with room; a size or a value fits one line. */
const MULTILINE_FROM = 300;

/** The reason the server gave, on one line; undefined when the failure carried none (no answer, a bare 5xx). */
const serverReasonOf = (err: unknown): string | undefined => {
  const message = isAxiosError<{ message?: string | string[] }>(err) ? err.response?.data?.message : undefined;
  const text = Array.isArray(message) ? message.join(' · ') : message;
  return text || undefined;
};

const failureOf = (err: unknown) => sendFailureOf(err, 'catalog request', serverReasonOf(err));

/** Every required field of the type has something in it: text that is not blank, or at least one choice. */
export const fieldsComplete = (spec: CatalogRequestTypeSpec | null, fields: CatalogRequestFields): boolean =>
  spec !== null &&
  spec.fields.every((field) => {
    if (!field.required) {
      return true;
    }
    const value = fields[field.key];
    return Array.isArray(value) ? value.length > 0 : typeof value === 'string' && value.trim() !== '';
  });

/** The fields as the request sends them: trimmed, with blank optionals left out. */
export const fieldsToSend = (
  spec: CatalogRequestTypeSpec | null,
  fields: CatalogRequestFields,
): CatalogRequestFields => {
  const sent: CatalogRequestFields = {};
  for (const field of spec?.fields ?? []) {
    const value = fields[field.key];
    if (Array.isArray(value)) {
      if (value.length > 0) {
        sent[field.key] = value;
      }
    } else if (typeof value === 'string' && value.trim() !== '') {
      sent[field.key] = value.trim();
    }
  }
  return sent;
};

export interface CatalogRequestDialogProps {
  client: FulfillmentAdminClient;
  orderId: number;
  lines: FulfillmentOrderLine[];
  /** The line to start on, when the page already knows which (one line checked); null opens with none picked. */
  initialLineNo?: number | null;
  onClose: () => void;
  onSent: (message: string) => void;
}

interface FieldProps {
  field: CatalogRequestFieldSpec;
  value: string | string[] | undefined;
  onChange: (value: string | string[]) => void;
}

/** One field of the chosen type, drawn by its kind: a box, a list, checkboxes, a link box or a SKU box. */
function Field({ field, value, onChange }: FieldProps) {
  const id = `catalog-request-${field.key}`;
  const label = field.required
    ? field.label
    : field.label.endsWith('(optional)')
      ? field.label
      : `${field.label} (optional)`;
  if (field.kind === 'choice') {
    return (
      <label className="cq-field" htmlFor={id}>
        <span>{label}</span>
        <select id={id} value={typeof value === 'string' ? value : ''} onChange={(e) => onChange(e.target.value)}>
          <option value="">Choose one…</option>
          {(field.options ?? []).map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
    );
  }
  if (field.kind === 'choices') {
    const picked = Array.isArray(value) ? value : [];
    return (
      <fieldset className="cq-field cq-choices">
        <legend>{label}</legend>
        <div className="cq-checks">
          {(field.options ?? []).map((option) => (
            <label key={option.value}>
              <input
                type="checkbox"
                checked={picked.includes(option.value)}
                onChange={(e) =>
                  onChange(
                    e.target.checked ? [...picked, option.value] : picked.filter((item) => item !== option.value),
                  )
                }
              />
              {option.label}
            </label>
          ))}
        </div>
      </fieldset>
    );
  }
  const text = typeof value === 'string' ? value : '';
  const multiline = field.kind === 'text' && (field.maxLength ?? MULTILINE_FROM) >= MULTILINE_FROM;
  return (
    <label className="cq-field" htmlFor={id}>
      <span>{label}</span>
      {multiline ? (
        <textarea
          id={id}
          rows={3}
          value={text}
          placeholder={field.placeholder}
          maxLength={field.maxLength}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <input
          id={id}
          type={field.kind === 'url' ? 'url' : 'text'}
          inputMode={field.kind === 'sku' ? 'numeric' : undefined}
          value={text}
          placeholder={field.placeholder ?? (field.kind === 'url' ? 'https://…' : undefined)}
          maxLength={field.maxLength}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </label>
  );
}

/**
 * Asks the catalog team something about one line (SSD-7384): pick the line, the kind of request and what that kind
 * needs, and send. The types and their fields come from the server's schema, so the form never offers a field the
 * server would refuse, and the message below the fields is built by the server from the same code as the post. The
 * line picker is the COGS report's. Nothing is picked in advance, so a request is never sent by habit.
 */
export function CatalogRequestDialog({
  client,
  orderId,
  lines,
  initialLineNo = null,
  onClose,
  onSent,
}: CatalogRequestDialogProps) {
  const dialogRef = useModalDialog(onClose);
  const [schema, setSchema] = useState<CatalogRequestSchema | null | undefined>(undefined);
  const [lineNo, setLineNo] = useState<number | null>(initialLineNo);
  const [group, setGroup] = useState<string | null>(null);
  const [type, setType] = useState<string | null>(null);
  const [fields, setFields] = useState<CatalogRequestFields>({});
  const [preview, setPreview] = useState<CatalogRequestPreview | null | undefined>(undefined);
  // Why the last preview was refused, when the server said (a link on the wrong host); '' when it did not.
  const [previewProblem, setPreviewProblem] = useState('');
  const [sending, setSending] = useState(false);
  const [refusal, setRefusal] = useState('');
  // Kept through edits until the dialog closes: an edited Send after it may post a second message.
  const [unsure, setUnsure] = useState('');

  const picked = useMemo(() => lines.find((line) => line.lineNo === lineNo) ?? null, [lines, lineNo]);
  const typesInGroup = useMemo(
    () => (schema && group ? schema.types.filter((spec) => spec.group === group) : []),
    [schema, group],
  );
  const spec = useMemo(
    () => (schema && type ? (schema.types.find((candidate) => candidate.type === type) ?? null) : null),
    [schema, type],
  );
  const complete = picked !== null && fieldsComplete(spec, fields);
  const sent = useMemo(() => fieldsToSend(spec, fields), [spec, fields]);
  const sentKey = JSON.stringify(sent);
  const requestIdFor = useRequestIdForSend();

  useEffect(() => {
    let ignore = false;
    client
      .getCatalogRequestSchema()
      .then((view) => !ignore && setSchema(view))
      .catch(() => !ignore && setSchema(null));
    return () => {
      ignore = true;
    };
  }, [client]);

  useEffect(() => {
    if (!schema || picked === null || spec === null || !complete) {
      setPreview(undefined);
      setPreviewProblem('');
      return undefined;
    }
    let ignore = false;
    const timer = setTimeout(() => {
      client
        .previewCatalogRequest(orderId, picked.lineNo, {
          schemaVersion: schema.version,
          type: spec.type,
          fields: JSON.parse(sentKey),
        })
        .then((view) => {
          if (!ignore) {
            setPreview(view);
            setPreviewProblem('');
          }
        })
        .catch((err: unknown) => {
          if (!ignore) {
            setPreview(null);
            setPreviewProblem(serverReasonOf(err) ?? '');
          }
        });
    }, PREVIEW_DELAY_MS);
    return () => {
      ignore = true;
      clearTimeout(timer);
    };
  }, [client, orderId, schema, picked, spec, complete, sentKey]);

  const chooseGroup = (next: string) => {
    setGroup(next);
    setFields({});
    setRefusal('');
    // A group with one kind of request (Other) needs no second choice.
    const only = schema?.types.filter((candidate) => candidate.group === next) ?? [];
    setType(only.length === 1 ? only[0]!.type : null);
  };

  const chooseType = (next: string) => {
    setType(next || null);
    setFields({});
    setRefusal('');
  };

  const setField = (key: string, value: string | string[]) => {
    setFields((current) => ({ ...current, [key]: value }));
    setRefusal('');
  };

  // Fields the server has just refused would be refused again by Send, so it waits until they change.
  const blocked = sending || !schema || !complete || spec === null || previewProblem !== '';

  const submit = async () => {
    if (blocked || picked === null || spec === null || !schema) {
      return;
    }
    setSending(true);
    setRefusal('');
    try {
      const request = { schemaVersion: schema.version, type: spec.type, fields: sent };
      const requestId = requestIdFor(JSON.stringify([orderId, picked.lineNo, request]));
      await client.sendCatalogRequest(orderId, picked.lineNo, { ...request, requestId });
      onSent(`Catalog request for line ${picked.lineNo} (${picked.sku}) sent to ${CATALOG_REQUEST_CHANNEL}.`);
      onClose();
    } catch (err) {
      const failure = failureOf(err);
      if (failure.unsure) {
        setUnsure(failure.text);
      } else {
        setRefusal(failure.text);
      }
    } finally {
      setSending(false);
    }
  };

  const previewNote =
    schema === null
      ? 'Could not load the request types. Close and open again.'
      : picked === null
        ? 'Pick the product first.'
        : spec === null
          ? 'Choose what you are asking.'
          : !complete
            ? 'Fill in the fields above to see the message.'
            : preview === undefined
              ? 'Building the preview…'
              : preview === null
                ? // The server's own reason when it refused the fields; the plain note when it just did not answer.
                  previewProblem || 'Could not build the preview. Send still works.'
                : '';

  return (
    <dialog className="wms-prompt catalog-post catalog-request" ref={dialogRef} aria-label={CATALOG_REQUEST_TITLE}>
      <header>
        {CATALOG_REQUEST_TITLE}
        <button type="button" className="x" aria-label="Close" onClick={onClose}>
          ✕
        </button>
      </header>
      <div className="body">
        <p className="cr-lead">
          Sends one message to {CATALOG_REQUEST_CHANNEL} tagging the catalog team, with the order and the product filled
          in for you.
        </p>
        <LinePicker
          lines={lines}
          selectedLineNo={lineNo}
          onSelect={(line) => setLineNo(line.lineNo)}
          radioName="catalog-request-line"
        />
        <div className="cq-request" role="radiogroup" aria-label="Request">
          <span className="cq-label">Request</span>
          {(schema?.groups ?? []).map((candidate) => (
            <button
              key={candidate.group}
              type="button"
              role="radio"
              aria-checked={group === candidate.group}
              className={`cq-chip${group === candidate.group ? ' cq-chip-on' : ''}`}
              onClick={() => chooseGroup(candidate.group)}
            >
              {candidate.label}
            </button>
          ))}
          {schema === undefined && <span className="cr-preview-note">Loading the request types…</span>}
        </div>
        {group && typesInGroup.length > 1 && (
          <label className="cq-field" htmlFor="catalog-request-type">
            <span>What</span>
            <select id="catalog-request-type" value={type ?? ''} onChange={(e) => chooseType(e.target.value)}>
              <option value="">Choose one…</option>
              {typesInGroup.map((candidate) => (
                <option key={candidate.type} value={candidate.type}>
                  {candidate.label}
                </option>
              ))}
            </select>
          </label>
        )}
        {spec && (
          <div className="cq-fields" data-testid="catalog-request-fields">
            {spec.fields.map((field) => (
              <Field
                key={field.key}
                field={field}
                value={fields[field.key]}
                onChange={(value) => setField(field.key, value)}
              />
            ))}
          </div>
        )}
        <section className="cr-preview" aria-label="Message preview">
          <div className="cr-preview-title">What Send posts to {CATALOG_REQUEST_CHANNEL}</div>
          <div className="cr-preview-box" data-testid="catalog-request-preview">
            {previewNote && <div className="cr-preview-note">{previewNote}</div>}
            {!previewNote &&
              preview &&
              preview.text.split('\n').map((line, index) => (
                <div key={index} className="cr-preview-line">
                  {slackLineParts(line, preview.mentionNames)}
                </div>
              ))}
          </div>
        </section>
        {refusal && (
          <div className="cr-refusal" role="alert">
            {refusal}
          </div>
        )}
        {unsure && (
          <div className="cr-refusal" role="alert">
            {unsure}
          </div>
        )}
      </div>
      <div className="foot">
        <button type="button" className="btn btn-line btn-sm" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="btn btn-blue btn-sm" disabled={blocked} onClick={() => void submit()}>
          {sending ? 'Sending…' : `Send to ${CATALOG_REQUEST_CHANNEL}`}
        </button>
      </div>
    </dialog>
  );
}
